import { FormControlStatus } from '@angular/forms';
import { ReportFormRuleTemplate, RuleContext } from '@navstation-portal/reporting/data-model/ui';
import { FormState, ReportTabRulesKey, StateFieldType } from '@navstation-portal/reporting/data-model/vessel-report';
import { evaluate, resolveValue } from '@navstation-portal/reporting/util/form-rules';
import { formatCalculatedValuePath } from './create-form-store-events';
import { findField, isFieldOnScreen } from './form-rule-lookup.utility';
import { isEmptyValue } from './form-validity.utility';
import { CalculatedValue, getCalculatedValueField } from './with-calculated-values';

/** The Calculated Values of one tab store, and the form state that holds them. */
export interface CalculatedValueSource {
  formState: FormState;
  calculatedValues: CalculatedValue[];
}

/** One Calculated Value, and the status the store writes for it. */
export interface CalculatedValueStatus {
  tabId: ReportTabRulesKey;
  sectionId: string;
  fieldId: string;
  index?: number;
  property?: string;
  status: FormControlStatus;
}

/** What the last run saw for one Calculated Value. */
export interface JudgedValue {
  value: StateFieldType;
  // True when the template gave INVALID. The store can then clear that
  // INVALID itself.
  invalidFromTemplate: boolean;
}

const isOutsideBounds = (value: StateFieldType, min: number | Date | undefined, max: number | Date | undefined) =>
  typeof value === 'number' && ((typeof min === 'number' && value < min) || (typeof max === 'number' && value > max));

/**
 * The status that the template gives one Calculated Value, whether or not its
 * tab is rendered. A value the user cannot see is DISABLED, so it never blocks
 * the submit. A required value that is empty, or a value outside the min or
 * max of the template, is INVALID.
 */
const statusFromTemplate = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  tabId: ReportTabRulesKey,
  sectionId: string,
  fieldId: string,
  value: StateFieldType
): FormControlStatus => {
  if (!isFieldOnScreen(template, context, tabId, sectionId, fieldId)) return 'DISABLED';

  const rules = findField(template, tabId, sectionId, fieldId);
  const isRequired = rules?.isFieldRequired ? evaluate(rules.isFieldRequired, context) : false;
  if (isRequired && isEmptyValue(value)) return 'INVALID';

  if (isOutsideBounds(value, resolveValue(rules?.min, context), resolveValue(rules?.max, context))) return 'INVALID';

  return 'VALID';
};

/**
 * Finds each Calculated Value whose stored status must change, and gives the
 * status the store writes in its place. A Calculated Value changes when an
 * input on another tab changes, so its control is often not rendered to give
 * a status.
 *
 * It judges the value that the store holds, because that is the value the
 * report saves.
 *
 * A rendered control also checks a bound in its markup, and validators that
 * the template does not hold. The store cannot see those. So the store clears
 * an INVALID only in these cases:
 *
 * - The template gave the INVALID at the last run.
 * - The value changed since the last run. A rendered control then checks the
 *   new value after the store, and its status replaces the status of the store.
 * - There was no last run. The INVALID is then the status that init stamped.
 *
 * Pass `judged` from the last run, and keep the `judged` that it returns for
 * the next run.
 */
export const findCalculatedValueStatuses = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  sources: Partial<Record<ReportTabRulesKey, CalculatedValueSource>>,
  judged: ReadonlyMap<string, JudgedValue> = new Map()
): { statuses: CalculatedValueStatus[]; judged: Map<string, JudgedValue> } => {
  const statuses: CalculatedValueStatus[] = [];
  const nextJudged = new Map<string, JudgedValue>();

  for (const [tabId, source] of Object.entries(sources) as [ReportTabRulesKey, CalculatedValueSource][]) {
    for (const calculated of source.calculatedValues) {
      const entry = getCalculatedValueField(source.formState, calculated);
      if (!entry) continue;

      const key = `${tabId}.${formatCalculatedValuePath(calculated)}`;
      const last = judged.get(key);
      const rule = calculated.rule ?? calculated;
      const ruleTabId = calculated.rule?.tabId ?? tabId;
      const status = statusFromTemplate(template, context, ruleTabId, rule.sectionId, rule.fieldId, entry.value);
      nextJudged.set(key, { value: entry.value, invalidFromTemplate: status === 'INVALID' });

      if (status === entry.status) continue;

      const mayClearInvalid = !last || last.invalidFromTemplate || !Object.is(last.value, entry.value);
      if (status === 'VALID' && entry.status === 'INVALID' && !mayClearInvalid) continue;

      const { sectionId, fieldId, index, property } = calculated;
      statuses.push({ tabId, sectionId, fieldId, index, property, status });
    }
  }

  return { statuses, judged: nextJudged };
};
